/**
 * Built-in theme palettes (见 13 / §5.10). Two-tier design tokens: each
 * theme has a primitive `base` palette (raw Figma values — colors AND sizes) and
 * a semantic `tokens` map that references the primitives via
 * `var(--app-base-<name>)`. So adjusting a brand/neutral color or a spacing step
 * is a single edit in `base`, and every token using it updates. All types live in
 * `theme-contract.ts` (now open `Record<string, string>`, not closed unions —
 * the names/values are data-driven, see below).
 *
 * The palettes are authored as DATA in `./themes/light.json` / `./themes/dark.json`
 * (each `{ base, tokens }`); keeping them as JSON makes a palette change a data
 * edit (no TS) and a clean design handoff. They are derived from the Figma token
 * exports by these naming rules:
 *
 * - Color primitives (`base`, per light/dark mode): each Figma color variable
 *   becomes a base color; a trailing `-NN%` alpha suffix is rewritten to `-aNN`
 *   (e.g. `gray-1-90%` -> `gray-1-a90`); alpha < 1 values are emitted as
 *   `rgba(...)`, opaque ones as hex.
 * - Size primitives (`base`): `size-N` (number -> `<n>px`), referenced by the
 *   semantic spacing tokens.
 * - Semantic tokens (`tokens`): color tokens map per theme to a base color
 *   (light backgrounds use the light end of the neutral ramp, dark the dark end);
 *   spacing tokens (`layout` / `padding` / `margin`) map to a `size-N`. T-shirt
 *   sizes collapse repeated x's to a numeric prefix: `xxxs` -> `3xs`, `xxl` ->
 *   `2xl`, … (`xs`/`xl`/`s`/`m`/`l` unchanged). Every token value is a direct
 *   `var(--app-base-<name>)` reference, e.g. `layout-3xs` -> `var(--app-base-size-8)`.
 *
 * Sizes are mode-independent, so light.json and dark.json carry identical size
 * entries; only the color values differ between modes.
 */
import {
  accentPresetColor,
  accentTokenOverrides,
  deriveAccentBase,
} from './theme-accent';
import {
  BUILTIN_THEME_DARK_ID,
  BUILTIN_THEME_LIGHT_ID,
  THEME_BASE_PREFIX,
  type ThemeAccent,
  type ThemeDefinition,
  type ThemePreference,
  type ThemeTokens,
  type ThemeType,
} from './theme-contract';
import darkTheme from './themes/dark.json';
import lightTheme from './themes/light.json';

// Palette data comes from the JSON sources; their inferred (string→string) types
// satisfy ThemeBaseColors / ThemeTokens, so no annotations or casts are needed.
const LIGHT_BASE = lightTheme.base;
const LIGHT_TOKENS = lightTheme.tokens;
const DARK_BASE = darkTheme.base;
const DARK_TOKENS = darkTheme.tokens;

/**
 * Built-in light theme. Semantic tokens reference the light base primitives via
 * `var(--app-base-*)`; overlay/shadows stay literal. Mirror of the `:root`
 * fallback in the renderer's `main.css`.
 */
export const LIGHT_THEME: ThemeDefinition = {
  id: BUILTIN_THEME_LIGHT_ID,
  type: 'light',
  base: LIGHT_BASE,
  tokens: LIGHT_TOKENS,
};

/** Built-in dark theme: dark Figma primitives + the dark semantic tokens. */
export const DARK_THEME: ThemeDefinition = {
  id: BUILTIN_THEME_DARK_ID,
  type: 'dark',
  base: DARK_BASE,
  tokens: DARK_TOKENS,
};

/** The two built-in, peer themes (见 13). Extensible: more named themes (and
 * third-party contributions) can join this set, each defining its base palette +
 * the semantic token mapping. */
export const BUILTIN_THEMES: ThemeDefinition[] = [LIGHT_THEME, DARK_THEME];

/** Matches a token value that references a base color (`var(--app-base-<name>)`). */
const BASE_VAR_RE = new RegExp(`^var\\(${THEME_BASE_PREFIX}(.+)\\)$`);

/**
 * Resolve a theme's semantic tokens to final color strings (见 13): any token
 * whose value is a `var(--app-base-<name>)` reference is replaced with the
 * concrete value from the theme's base palette. Used for sandboxed surfaces
 * (iframe / external via the SDK) which receive only the token map and have no
 * `--app-base-*` custom properties to resolve the references against. The
 * in-client renderer instead applies the base palette + the `var()` tokens
 * directly, preserving the runtime two-tier indirection.
 */
export function flattenTokens(theme: ThemeDefinition): ThemeTokens {
  const out: ThemeTokens = {};
  for (const name of Object.keys(theme.tokens)) {
    const value = theme.tokens[name];
    const match = BASE_VAR_RE.exec(value);
    const baseName = match?.[1];
    const resolved = baseName ? theme.base[baseName] : undefined;
    out[name] = resolved ?? value;
  }
  return out;
}

/**
 * Opaque CSS hex (`#RGB` / `#RRGGBB`). Electron's window brush is this format;
 * a `var()` or `rgba()` is not a background it can paint during a resize.
 */
const OPAQUE_HEX_COLOR = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/** Whether `value` is a color Electron can use as a window background brush. */
export function isOpaqueHexColor(value: unknown): value is string {
  return typeof value === 'string' && OPAQUE_HEX_COLOR.test(value);
}

/**
 * Native window fill for the gap a fast resize exposes (见 08 / 13). The OS
 * grows the client area a frame before Chromium paints; those pixels are the
 * `BrowserWindow` background, so they have to be the theme canvas
 * (`color-bg-canvas`), already flattened to an opaque hex.
 */
export function windowCanvasColor(theme: ThemeDefinition): string {
  const value = flattenTokens(theme)['color-bg-canvas'];
  if (!isOpaqueHexColor(value)) {
    throw new Error(
      `color-bg-canvas is not an opaque hex color: ${String(value)}`,
    );
  }
  return value;
}

/**
 * A built-in theme with an accent preset applied (见 13): the accent-derived
 * base colors (`brand*`, `accent-*`, see `theme-accent.ts`) replace the
 * palette's, and the preset's token overrides replace the semantic mapping. The
 * JSON palettes already hold the `brand`-derived values, so the default accent
 * yields a theme equal to the built-in one.
 */
export function applyAccent(
  theme: ThemeDefinition,
  accent: ThemeAccent,
): ThemeDefinition {
  return {
    ...theme,
    base: {
      ...theme.base,
      ...deriveAccentBase(accentPresetColor(accent, theme.type), theme.type),
    },
    tokens: { ...theme.tokens, ...accentTokenOverrides(accent, theme.type) },
  };
}

/**
 * Resolve a {@link ThemePreference} + the OS dark-mode state + the
 * {@link ThemeAccent} into the effective {@link ThemeDefinition} (见 13).
 * `system` picks dark/light from `osDark`; an explicit preference ignores it.
 * Lives with the palettes (not the contract) because it is logic over the
 * concrete built-in themes; pure, so it is unit-testable.
 */
export function resolveTheme(
  preference: ThemePreference,
  osDark: boolean,
  accent: ThemeAccent,
): ThemeDefinition {
  const type: ThemeType =
    preference === 'system' ? (osDark ? 'dark' : 'light') : preference;
  return applyAccent(type === 'dark' ? DARK_THEME : LIGHT_THEME, accent);
}
